import type { AnalystOverride } from "@/types/dashboard";

const ANALYST_KEY_PREFIX = "soc:analyst:";

// Load analyst override from local storage
export function loadAnalystOverride(dashboardId: string): AnalystOverride {
  try {
    const keyId = dashboardId || "global";
    const raw = localStorage.getItem(ANALYST_KEY_PREFIX + keyId);
    if (!raw) return { dashboardId: keyId, hiddenInstanceIds: [], sectionLayouts: {} };
    return JSON.parse(raw) as AnalystOverride;
  } catch {
    return { dashboardId, hiddenInstanceIds: [], sectionLayouts: {} };
  }
}

// Save analyst override to local storage
export function saveAnalystOverride(override: AnalystOverride): void {
  try {
    const keyId = override.dashboardId || "global";
    localStorage.setItem(ANALYST_KEY_PREFIX + keyId, JSON.stringify(override));
  } catch {}
}

// ID generation
export function generateId(prefix = "id"): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

// Find the first free position for a widget in a grid layout
export function findFreePosition(
  placements: { layout: { x: number; y: number; w: number; h: number } }[],
  w: number,
  h: number,
  cols = 12,
): { x: number; y: number } {
  if (placements.length === 0) return { x: 0, y: 0 };

  const maxRow = placements.reduce((acc, p) => Math.max(acc, p.layout.y + p.layout.h), 0) + h;

  // Build an occupancy set of "x,y" strings for quick lookup
  const occupied = new Set<string>();
  for (const p of placements) {
    for (let row = p.layout.y; row < p.layout.y + p.layout.h; row++) {
      for (let col = p.layout.x; col < p.layout.x + p.layout.w; col++) {
        occupied.add(`${col},${row}`);
      }
    }
  }

  for (let y = 0; y < maxRow; y++) {
    for (let x = 0; x <= cols - w; x++) {
      let fits = true;
      outer: for (let dy = 0; dy < h; dy++) {
        for (let dx = 0; dx < w; dx++) {
          if (occupied.has(`${x + dx},${y + dy}`)) {
            fits = false;
            break outer;
          }
        }
      }
      if (fits) return { x, y };
    }
  }

  // No gap found — append below
  return { x: 0, y: maxRow - h };
}

// Converts a string to a URL-friendly slug
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

// Ensures a slug is unique among existing slugs
export function ensureUniqueSlug(base: string, existingSlugs: string[]): string {
  const clean = slugify(base) || "dashboard";
  const taken = new Set(existingSlugs);
  if (!taken.has(clean)) return clean;
  for (let i = 2; i < 100; i++) {
    const candidate = `${clean}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${clean}-${Math.random().toString(36).slice(2, 5)}`;
}
